"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";

interface InterviewSheetContextType {
  // Sheet state
  sheetOpen: boolean;
  sheetContent: ReactNode | null;
  disableAnimations: boolean;
  isDismissible: boolean;

  // Actions
  openSheet: (
    content: ReactNode,
    options?: { disableAnimations?: boolean; isDismissible?: boolean },
  ) => void;
  closeSheet: () => void;
  updateSheetContent: (content: ReactNode) => void;
  setDismissHandler: (handler: (() => void) | null) => void;
  dismissHandler: (() => void) | null;
}

const InterviewSheetContext = createContext<
  InterviewSheetContextType | undefined
>(undefined);

export function InterviewSheetProvider({ children }: { children: ReactNode }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetContent, setSheetContent] = useState<ReactNode | null>(null);
  const [disableAnimations, setDisableAnimations] = useState(false);
  const [isDismissible, setIsDismissible] = useState(false);
  const [dismissHandler, setDismissHandler] = useState<(() => void) | null>(
    null,
  );
  const isInitialRender = useRef(true);

  const openSheet = useCallback(
    (
      content: ReactNode,
      options?: { disableAnimations?: boolean; isDismissible?: boolean },
    ) => {
      // Only disable animations on first render if explicitly requested
      if (options?.disableAnimations && isInitialRender.current) {
        setDisableAnimations(true);
      } else {
        setDisableAnimations(false);
      }

      // Set whether this sheet is dismissible (shows X button in header)
      setIsDismissible(options?.isDismissible ?? false);

      setSheetContent(content);
      setSheetOpen(true);

      // After first render, enable animations for all future opens
      if (isInitialRender.current) {
        isInitialRender.current = false;
      }
    },
    [],
  );

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    // Re-enable animations after closing
    setDisableAnimations(false);
    setIsDismissible(false);
    setDismissHandler(null);
  }, []);

  const updateSheetContent = useCallback((content: ReactNode) => {
    // Update content without changing open state or animations
    setSheetContent(content);
  }, []);

  return (
    <InterviewSheetContext.Provider
      value={{
        sheetOpen,
        sheetContent,
        disableAnimations,
        isDismissible,
        openSheet,
        closeSheet,
        updateSheetContent,
        setDismissHandler,
        dismissHandler,
      }}
    >
      {children}
    </InterviewSheetContext.Provider>
  );
}

export function useInterviewSheet() {
  const context = useContext(InterviewSheetContext);
  if (!context) {
    throw new Error(
      "useInterviewSheet must be used within InterviewSheetProvider",
    );
  }
  return context;
}
